import type {DataUploadClassOption} from 'common/helpers/dataUpload'

/** A class of the `resume-data.curriculums` tree, flattened (Step4Manual's `treeClasses`). */
export interface CurrentSchoolClass {
  classId: number
  name: string
  displayName: string
  /** resume-data `school_display_name`: the board prefix only where two boards share the name. */
  schoolDisplayName?: string
  curriculum: string
  curriculumShortCode: string
  curriculumKey: string | null
  level: string
  levelId: number | null
  branch: string
  branchId: number | null
}

/**
 * The classes a "Which class does the file mean?" question offers, as the school has them NOW.
 *
 * A question's `options` are the server's candidates from when the file was read, and they are
 * kept (in memory) while the user goes back to Academic Structure. A class deleted there, a
 * curriculum changed, or a class added since, is not reflected in them — so with the current
 * classes known, the choices are rebuilt from them:
 *  - every current class whose name matches the one the file wrote (`matchesWritten`), and
 *  - every original candidate that still exists, under its current name and curriculum;
 *  - when none of them is left, every current class, so the rows can still be placed.
 * Without the current classes (the tree not loaded yet) the original candidates stand.
 */
export const currentClassQuestionOptions = (
  original: readonly DataUploadClassOption[],
  classes: readonly CurrentSchoolClass[],
  matchesWritten: (cls: CurrentSchoolClass) => boolean
): DataUploadClassOption[] => {
  if (classes.length === 0) return [...original]
  const originalById = new Map(original.map((option) => [option.classId, option]))
  const chosen = classes.filter((cls) => originalById.has(cls.classId) || matchesWritten(cls))
  return (chosen.length > 0 ? chosen : classes).map((cls) => {
    const before = originalById.get(cls.classId)
    return {
      classId: cls.classId,
      name: cls.name,
      displayName: cls.displayName || cls.name,
      ...(cls.schoolDisplayName ? {schoolDisplayName: cls.schoolDisplayName} : {}),
      levelId: cls.levelId ?? before?.levelId ?? null,
      level: cls.level,
      curriculumKey: cls.curriculumKey ?? before?.curriculumKey ?? null,
      curriculum: cls.curriculum,
      curriculumShortCode: cls.curriculumShortCode,
      branchId: cls.branchId ?? before?.branchId ?? null,
      branch: cls.branch,
    }
  })
}
